import type { ReactNode } from 'react'
import {
  ActionIcon,
  Badge,
  Checkbox,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconEdit, IconTrash, IconUsers } from '@tabler/icons-react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Link } from 'react-router-dom'
import { Th } from '@/components'
import { SNOMED_USER_ROLES } from '@/utils'
import { describeDoseSpotClinicianKind } from '@/utils/doseSpotClinicianKind'
import type { PractitionerRow } from '@/hooks'
import { MemberQualificationsHoverCard } from './MemberQualificationsHoverCard'

type Props = {
  /** Carried into the detail link's query so "Back to Provider Operations" lands on this Organization. */
  organizationId: string
  members: PractitionerRow[]
  hasMore: boolean
  fetchNextPage: () => void
  searchActive: boolean
  nameSort: 'asc' | 'desc' | null
  onToggleNameSort: () => void
  selectedIds: Set<string>
  onToggle: (id: string) => void
  onToggleAll: (checked: boolean) => void
  onRemove: (member: PractitionerRow) => void
  emptyAction: ReactNode
}

const SCROLL_TARGET = 'org-members-scroll'

export function MembersTable({
  organizationId,
  members,
  hasMore,
  fetchNextPage,
  searchActive,
  nameSort,
  onToggleNameSort,
  selectedIds,
  onToggle,
  onToggleAll,
  onRemove,
  emptyAction,
}: Props) {
  const selectedLoaded = members.filter((m) => selectedIds.has(m.practitionerId)).length
  const allSelected = members.length > 0 && selectedLoaded === members.length
  const someSelected = selectedLoaded > 0 && !allSelected

  return (
    <div id={SCROLL_TARGET} className="h-[480px] overflow-y-auto">
      <InfiniteScroll
        dataLength={members.length}
        next={fetchNextPage}
        hasMore={hasMore}
        loader={
          <Group justify="center" p="sm">
            <Loader size="sm" />
          </Group>
        }
        scrollableTarget={SCROLL_TARGET}
        className="contents"
      >
        <Table stickyHeader withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th className="bg-[#fafafa]!" w={40}>
                <Checkbox
                  aria-label="Select all loaded members"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(event) => onToggleAll(event.currentTarget.checked)}
                />
              </Table.Th>
              <Th
                style={{ backgroundColor: '#fafafa' }}
                sorted={nameSort !== null}
                reversed={nameSort === 'desc'}
                onSort={onToggleNameSort}
              >
                Name
              </Th>
              <Table.Th className="bg-[#fafafa]!">Role</Table.Th>
              <Table.Th className="bg-[#fafafa]!">Email</Table.Th>
              <Table.Th className="bg-[#fafafa]!">DoseSpot</Table.Th>
              <Table.Th className="bg-[#fafafa]!">
                <div className="size-7" />
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {members.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Stack align="center" justify="center" gap="xs" mih={420}>
                    <IconUsers size={32} stroke={1.5} />
                    <Text>No members found</Text>
                    {searchActive ? (
                      <Text size="sm" c="dimmed">
                        Try adjusting your search
                      </Text>
                    ) : (
                      emptyAction
                    )}
                  </Stack>
                </Table.Td>
              </Table.Tr>
            ) : (
              members.map((member) => {
                const isHospitalManager = member.roles.some(
                  (r) => r.code === SNOMED_USER_ROLES.HOSPITAL_MANAGER.code,
                )
                const selected = selectedIds.has(member.practitionerId)
                return (
                  <Table.Tr
                    key={member.practitionerId}
                    bg={selected ? 'var(--mantine-color-blue-light)' : undefined}
                  >
                    <Table.Td>
                      <Checkbox
                        aria-label={`Select ${member.displayName || 'member'}`}
                        checked={selected}
                        onChange={() => onToggle(member.practitionerId)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Text span>{member.displayName || '—'}</Text>
                        {(member.licenses.length > 0 ||
                          member.npiNumber ||
                          member.professionalRole) && (
                          <MemberQualificationsHoverCard member={member} />
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {isHospitalManager ? (
                          <Badge size="sm" variant="outline">
                            Care Coordinator
                          </Badge>
                        ) : (
                          <Badge size="sm">Practitioner</Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {member.email ?? (
                        <Text span c="dimmed">
                          No email
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {member.doseSpotClinicianId && member.doseSpotClinicianKind ? (
                        <Group gap="xs" wrap="nowrap">
                          <Text span>{member.doseSpotClinicianId}</Text>
                          <Badge size="sm" variant="light">
                            {describeDoseSpotClinicianKind(member.doseSpotClinicianKind)}
                          </Badge>
                        </Group>
                      ) : (
                        <Text span c="dimmed">
                          Not linked
                        </Text>
                      )}
                    </Table.Td>

                    <Table.Td>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        {/* For a Care Coordinator the detail page holds only the DoseSpot
                            clinician id (see ProviderPage). */}
                        <Tooltip label="Edit credentials">
                          <ActionIcon
                            component={Link}
                            to={{
                              pathname: `/providers/${member.practitionerId}`,
                              search: new URLSearchParams({ organizationId }).toString(),
                            }}
                            variant="subtle"
                            aria-label="Edit credentials"
                            // This table lives inside the Open Members modal, which the
                            // modals manager keeps mounted across routes.
                            onClick={() => modals.closeAll()}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Remove from organization">
                          <ActionIcon
                            variant="outline"
                            color="red"
                            aria-label="Remove member"
                            onClick={() => onRemove(member)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )
              })
            )}
          </Table.Tbody>
        </Table>
      </InfiniteScroll>
    </div>
  )
}
